import {
  ArrowRight,
  BookOpen,
  ChevronRight,
  ExternalLink,
  Info,
  MessageSquare,
  Reply,
  Settings,
  Zap,
} from "lucide-react";
import { cx } from "../../utils";
import Card from "../../components/ui/Card";

/**
 * The "How to use" tab: where each feature lives in the CRM once setup is
 * done, and how to use it.
 *
 * Content comes from the app record (Apps.usage, written by
 * backend/scripts/usage-copy.js), so every app's guide shares this layout:
 * the path to the feature, numbered steps, the app's actions, and tips.
 */

const ICONS = {
  message: MessageSquare,
  reply: Reply,
  workflow: Zap,
  settings: Settings,
};

/**
 * The CRM's own origin, so a link opens the page on whatever white-label domain
 * the customer is using rather than one we picked. This page is framed by the
 * CRM, so the parent frame's origin is the answer; ancestorOrigins is the
 * direct read, referrer the fallback where it isn't supported. Outside a frame
 * (local development) there is nothing to read, so it falls back to the
 * platform's default domain.
 */
const DEFAULT_CRM_ORIGIN = "https://app.gohighlevel.com";

const crmOrigin = () => {
  const framed = (() => {
    try {
      return window.self !== window.top;
    } catch {
      return true; // cross-origin parent: reading window.top throws
    }
  })();
  if (!framed) return DEFAULT_CRM_ORIGIN;

  try {
    const ancestors = window.location.ancestorOrigins;
    if (ancestors && ancestors.length > 0) return ancestors[0];
  } catch {
    /* fall through */
  }
  try {
    if (document.referrer) return new URL(document.referrer).origin;
  } catch {
    /* fall through */
  }
  return DEFAULT_CRM_ORIGIN;
};

// A page in the customer's own account. Needs a location: an agency-level
// install has no single account to open, so it gets no link.
// Only a path on the CRM itself ("/v2/…"): anything else joined onto the
// origin could point at another site.
const linkFor = (link, locationId) =>
  link?.path && locationId && link.path.startsWith("/") && !link.path.startsWith("//")
    ? crmOrigin() + link.path.replace("{locationId}", encodeURIComponent(locationId))
    : null;

// Screenshots are web images only — the image is also a link, and a
// javascript: address there would run script.
const safeImage = (src) => (/^https?:\/\//i.test(String(src || "")) ? src : null);

// "Where to find it", drawn as the trail of clicks it describes.
const Path = ({ crumbs }) => (
  <div className="flex flex-wrap items-center gap-1.5">
    {crumbs.map((crumb, i) => (
      <span key={i} className="inline-flex items-center gap-1.5">
        {i > 0 && <ChevronRight size={12} className="text-ink-300" aria-hidden />}
        <span className="rounded-md border border-ink-200 bg-ink-50 px-2 py-0.5 text-[12px] font-medium text-ink-700">
          {crumb}
        </span>
      </span>
    ))}
  </div>
);

const Section = ({ section, locationId }) => {
  const Icon = ICONS[section.icon] || BookOpen;
  const steps = section.steps || [];
  const actions = section.actions || [];
  const tips = section.tips || [];
  const href = linkFor(section.link, locationId);
  const imageSrc = safeImage(section.image?.src);

  return (
    <section id={`usage-${section.id}`} className="scroll-mt-6">
      <Card padded={false}>
        <div className="flex items-start gap-3.5 px-5 pt-5">
          <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-gradient-to-br from-brand-500 to-violet-600 text-white shadow-brand-glow">
            <Icon size={17} />
          </span>
          <div className="min-w-0">
            <h3 className="text-[16px] font-semibold tracking-tight text-ink-900">
              {section.title}
            </h3>
            {section.summary && (
              <p className="mt-0.5 text-[13px] leading-relaxed text-ink-500">
                {section.summary}
              </p>
            )}
          </div>
        </div>

        <div className="space-y-5 px-5 pb-5 pt-4">
          {section.where?.length > 0 && (
            <div>
              <p className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-ink-400">
                Where to find it
              </p>
              <Path crumbs={section.where} />
              {/* New tab rather than the CRM's own frame: a framed page may not
                  be allowed to navigate its parent, and the customer keeps this
                  guide open beside the page it describes. */}
              {href && (
                <a
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-3 inline-flex h-8 items-center gap-1.5 rounded-lg bg-gradient-to-br from-brand-500 to-violet-600 px-3 text-[12.5px] font-semibold text-white shadow-brand-glow transition-all hover:brightness-110 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-500"
                >
                  {section.link.label || "Open"}
                  <ExternalLink size={13} aria-hidden />
                </a>
              )}
            </div>
          )}

          {/* A marked-up screenshot of the page the steps describe. */}
          {imageSrc && (
            <figure className="overflow-hidden rounded-xl border border-ink-200/70 bg-ink-50">
              <a href={imageSrc} target="_blank" rel="noopener noreferrer">
                <img
                  src={imageSrc}
                  alt={section.image.alt || section.title}
                  loading="lazy"
                  className="block w-full"
                />
              </a>
              {section.image.caption && (
                <figcaption className="border-t border-ink-200/70 bg-white px-3.5 py-2 text-[12px] text-ink-500">
                  {section.image.caption}
                </figcaption>
              )}
            </figure>
          )}

          {steps.length > 0 && (
            <div>
              <p className="mb-2.5 text-[11px] font-semibold uppercase tracking-wider text-ink-400">
                How to use it
              </p>
              <ol className="space-y-2.5">
                {steps.map((step, i) => (
                  <li key={i} className="flex gap-3">
                    <span className="mt-0.5 grid h-5 w-5 shrink-0 place-items-center rounded-full bg-brand-50 text-[11px] font-semibold text-brand-700 ring-1 ring-brand-200/60">
                      {i + 1}
                    </span>
                    <span className="text-[13px] leading-relaxed text-ink-700">{step}</span>
                  </li>
                ))}
              </ol>
            </div>
          )}

          {actions.length > 0 && (
            <div>
              <p className="mb-2.5 text-[11px] font-semibold uppercase tracking-wider text-ink-400">
                Available actions
              </p>
              <ul className="divide-y divide-ink-150 overflow-hidden rounded-xl border border-ink-200/70">
                {actions.map((action, i) => (
                  <li key={i} className="flex gap-3 bg-white px-3.5 py-2.5">
                    <Zap size={13} className="mt-1 shrink-0 text-violet-500" aria-hidden />
                    <div className="min-w-0">
                      <p className="text-[13px] font-medium text-ink-900">{action.name}</p>
                      {action.description && (
                        <p className="mt-0.5 text-[12.5px] leading-relaxed text-ink-500">
                          {action.description}
                        </p>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {tips.length > 0 && (
            <ul className="space-y-2 rounded-xl bg-ink-50 px-3.5 py-3">
              {tips.map((tip, i) => (
                <li key={i} className="flex gap-2.5 text-[12.5px] leading-relaxed text-ink-600">
                  <Info size={13} className="mt-0.5 shrink-0 text-ink-400" aria-hidden />
                  <span>{tip}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </Card>
    </section>
  );
};

const UsagePanel = ({ app, ready, onOpenSetup, locationId }) => {
  const sections = app?.usage?.sections || [];

  const jump = (id) => {
    document.getElementById(`usage-${id}`)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_18rem] 2xl:grid-cols-[minmax(0,1fr)_22rem]">
      <div className="min-w-0 space-y-5">
        {app.usage?.intro && (
          <p className="max-w-2xl text-sm leading-relaxed text-ink-500">{app.usage.intro}</p>
        )}
        {sections.map((section) => (
          <Section key={section.id} section={section} locationId={locationId} />
        ))}
      </div>

      <aside className="space-y-5 lg:sticky lg:top-20 lg:self-start">
        {/* Nothing below works until setup is finished, so say so first. */}
        {!ready && (
          <section className="overflow-hidden rounded-2xl border border-amber-200/80 bg-white shadow-card">
            <div className="bg-gradient-to-br from-amber-50 to-orange-50 px-5 py-4">
              <p className="text-[13.5px] font-semibold text-amber-900">Finish setup first</p>
              <p className="mt-1 text-[12.5px] leading-relaxed text-amber-800/90">
                These features start working once your credentials are saved and the webhook is registered.
              </p>
            </div>
            <div className="p-4">
              <button
                type="button"
                onClick={onOpenSetup}
                className="inline-flex h-9 w-full items-center justify-center gap-1.5 rounded-xl bg-gradient-to-br from-brand-500 to-violet-600 text-[13px] font-semibold text-white shadow-brand-glow transition-all hover:brightness-110"
              >
                Continue setup <ArrowRight size={14} />
              </button>
            </div>
          </section>
        )}

        <Card title="On this page" bodyClassName="pt-1">
          <nav className="space-y-0.5" aria-label="How to use sections">
            {sections.map((section) => {
              const Icon = ICONS[section.icon] || BookOpen;
              return (
                <button
                  key={section.id}
                  type="button"
                  onClick={() => jump(section.id)}
                  className={cx(
                    "flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left text-[13px] text-ink-600",
                    "transition-colors hover:bg-ink-50 hover:text-ink-900",
                  )}
                >
                  <Icon size={14} className="shrink-0 text-ink-400" />
                  <span className="min-w-0 truncate">{section.title}</span>
                </button>
              );
            })}
          </nav>
        </Card>
      </aside>
    </div>
  );
};

export default UsagePanel;
